import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatApiErrorDetail } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2 } from 'lucide-react';
import { CAT } from '@/constants/testIds';
import { toast } from 'sonner';

export default function CategoriesPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('EXPENSE');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'EXPENSE' });

  const { data } = useQuery({ queryKey: ['categories'], queryFn: async () => (await api.get('/categories')).data });
  const items = (data || []).filter((c) => c.type === tab && c.active !== false);

  const saveMut = useMutation({
    mutationFn: async (payload) => (await api.post('/categories', payload)).data,
    onSuccess: () => { toast.success('Salvo'); setOpen(false); qc.invalidateQueries({ queryKey: ['categories'] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/categories/${id}`)).data,
    onSuccess: () => { toast.success('Removida'); qc.invalidateQueries({ queryKey: ['categories'] }); },
  });

  return (
    <div data-testid={CAT.page} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">Classificação</div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Categorias</h1>
        </div>
        <Button data-testid={CAT.newBtn} onClick={() => { setForm({ name: '', type: tab }); setOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="h-4 w-4 mr-2" /> Nova</Button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="EXPENSE">Despesas</TabsTrigger>
          <TabsTrigger value="INCOME">Receitas</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        {items.map((c) => (
          <Card key={c.id} className="p-4 flex items-center gap-3 card-elevate border-border/70">
            <div className="h-2 w-2 rounded-full" style={{ background: c.color || (tab === 'INCOME' ? '#10B981' : '#F43F5E') }} />
            <div className="flex-1 truncate font-medium">{c.name}</div>
            <Button size="icon" variant="ghost" className="text-rose-500 h-8 w-8" onClick={() => { if (confirm('Remover?')) delMut.mutate(c.id); }}><Trash2 className="h-4 w-4" /></Button>
          </Card>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nova categoria</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate(form); }} className="space-y-4">
            <div className="space-y-2"><Label>Nome</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="EXPENSE">Despesa</SelectItem><SelectItem value="INCOME">Receita</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
